/**
 * Core domain types.
 *
 * The mental model is deliberately simple and is the reason scenarios are
 * cheap to reason about, diff, export and hand to an LLM:
 *
 *   effective org = base snapshot (from Snowflake)
 *                 + scenario.newPeople   (planned hires)
 *                 + scenario.edits       (per-person field overrides)
 *                 + scenario.departures  (people flagged as leaving)
 *
 * Nothing is ever mutated in the base snapshot, so "has this person moved?"
 * is always answerable by comparing effective state to base state.
 *
 * Squads work the same way, except that they fold *twice* — because there are
 * two different questions to answer about a team, and only one of them is a
 * plan:
 *
 *   derived squads  = one flat team per distinct value of the TEAMS column
 *   base squads     = derived + the base overrides   (how the teams really are)
 *   effective squads = base + the scenario's layer   (how a plan says they should be)
 *
 * Both folds are the same operation — `effectiveSquads` applied to a
 * `SquadLayer` — so there is one set of rules about what an override means.
 *
 * The split exists because Snowflake only knows a flat list of team names. It
 * has no idea that Athena, Theia and Themis sit inside Data, who leads them or
 * what colour they are on a Jira board. That curation lives in the overrides
 * and survives every refresh; everyone the overrides say nothing about renders
 * exactly as Snowflake has them.
 */
export const SQUAD_ROLES = ['lead', 'product', 'design', 'engineer', 'member'];
export const GROUP_BY_KEYS = [
    'org',
    'department',
    'team',
    'squad',
    'location',
    'country',
];
/** Narrowing helper: person changes carry a personId, team changes may not. */
export function isSquadChange(change) {
    return change.kind.startsWith('squad-');
}
/**
 * The person a change is about, or null when it is only about a team.
 *
 * Everything that pans to, highlights or reveals the subject of a change goes
 * through this, so "a new team called Orion" never has to pretend to be about
 * somebody.
 */
export function changePersonId(change) {
    if (!isSquadChange(change))
        return change.personId;
    return change.kind === 'squad-member' ? change.personId : null;
}
//# sourceMappingURL=types.js.map